import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { try_for_free } from "../../url";
import RefSiteFooter from "./RefSiteFooter";
import RefPricingTeaser from "./RefPricingTeaser";

// Assets
import resellers from "../../Assets/resellers.png";
import non_profits from "../../Assets/non_profits.png";
import partnersBg from "../../Assets/partners.png";

const PARTNER_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rspa-ink: #163A5A; --rspa-ink2: #3D6A8C; --rspa-ink3: #5E87A3;
    --rspa-sky: #1A63A5; --rspa-mint: #2AB6E8;
    --rspa-secondary: #2AB6E8;
    --rspa-secondary-deep: #1AA3D4;
    --rspa-secondary-soft: #D9F4FC;
    --rspa-secondary-muted: rgba(42,182,232,0.18);
    --rspa-sky-light: #E8F4FC;
    --rspa-sky-hover: #155a94;
    --rspa-bg-dark: #0E2D48;
    --rspa-white: #FFFFFF; --rspa-surface: #F5F9FC;
    --rspa-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rspa-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rspa-page { font-family: var(--rspa-ff-body); color: var(--rspa-ink); background: var(--rspa-white); }

  /* HERO */
  .rspa-hero {
    text-align: center; padding: 64px 24px 0px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rspa-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
  }
  .rspa-hero::before {
    content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
    width: 900px; height: 500px;
    background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .rspa-hero-orb {
    position: absolute; width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(42,182,232,0.15) 0%, transparent 60%);
    top: -100px; right: -100px; z-index: 0; filter: blur(20px);
  }
  .rspa-hero-content { position: relative; z-index: 1; }
  .rspa-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, var(--rspa-sky-light) 0%, var(--rspa-secondary-soft) 100%);
    color: var(--rspa-sky); border: 1px solid var(--rspa-secondary-muted);
    font-size: 12px; font-weight: 600; padding: 8px 16px;
    border-radius: 100px; margin-bottom: 24px; letter-spacing: .06em;
    box-shadow: 0 4px 12px rgba(42,182,232,0.1);
    text-transform: uppercase;
  }
  .rspa-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rspa-secondary); animation: rspa-pulse 2s infinite; }
  @keyframes rspa-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.8)} }
  .rspa-h1 { font-family: var(--rspa-ff-head); font-size: 60px; font-weight: 800; letter-spacing: -.02em; line-height: 1.05; margin-bottom: 20px; }
  .rspa-h1-grad { background: linear-gradient(135deg, var(--rspa-sky), var(--rspa-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rspa-hero-sub { font-size: 18px; color: var(--rspa-ink2); font-weight: 400; max-width: 580px; margin: 0 auto 32px; line-height: 1.6; }
  .rspa-cta-row { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 40px; }
  .rspa-btn-link { background: transparent; color: var(--rspa-sky); font-size: 14px; font-weight: 600; padding: 14px 0; border: none; cursor: pointer; transition: color .2s; }
  .rspa-btn-link:hover { color: var(--rspa-sky-hover); }

  .rspa-trust { margin-top: 48px; padding-top: 32px; border-top: 1px solid #E8EAF2; max-width: 800px; margin-left: auto; margin-right: auto; }
  .rspa-trust-label { font-size: 11px; color: var(--rspa-ink3); letter-spacing: .06em; font-weight: 500; margin-bottom: 12px; }
  .rspa-trust-logos { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
  .rspa-trust-logo {
    background: var(--rspa-surface); border: 1px solid #E8EAF2; border-radius: 6px;
    padding: 4px 12px; font-size: 11px; font-weight: 600; color: var(--rspa-ink3);
  }

  /* SECTIONS */
  .rspa-section { padding: 64px 24px; max-width: 1200px; margin: 0 auto; box-sizing: border-box; }
  .rspa-section-alt { background: var(--rspa-bg-dark); padding: 64px 40px; }
  .rspa-section-alt-inner { max-width: 1200px; margin: 0 auto; }
  .rspa-section-alt .rspa-h2 { color: #fff; }
  .rspa-section-alt .rspa-p { color: rgba(255,255,255,0.7); }
  .rspa-section-alt .rspa-section-label { background: rgba(42,182,232,.15); color: var(--rspa-mint); }

  .rspa-grid { display: grid; grid-template-columns: 1fr 1.15fr; gap: 72px; align-items: center; margin-bottom: 100px; }
  .rspa-grid:last-child { margin-bottom: 0; }
  .rspa-grid.reverse { grid-template-columns: 1.15fr 1fr; direction: rtl; }
  .rspa-grid.reverse > * { direction: ltr; }

  .rspa-text-block { position: relative; z-index: 2; }
  .rspa-section-label { display: inline-block; font-size: 11px; font-weight: 700; color: var(--rspa-sky); background: var(--rspa-sky-light); padding: 4px 10px; border-radius: 6px; letter-spacing: .08em; margin-bottom: 16px; text-transform: uppercase; }
  .rspa-h2 { font-family: var(--rspa-ff-head); font-size: 42px; font-weight: 800; line-height: 1.12; letter-spacing: -.02em; color: var(--rspa-ink); margin-bottom: 24px; }
  .rspa-p { font-size: 17px; color: var(--rspa-ink2); line-height: 1.7; margin-bottom: 20px; font-weight: 400; }
  
  .rspa-image-wrap { 
    position: relative; border-radius: 20px; z-index: 1; 
    transition: all 0.25s; border: 1px solid transparent;
  }
  .rspa-image-wrap:hover { 
    transform: translateY(-4px); 
    box-shadow: 0 16px 40px rgba(26,99,165,.1); 
    border-color: rgba(26,99,165,.28); 
  }
  .rspa-img { width: 100%; height: auto; display: block; border-radius: 20px; box-shadow: 0 24px 64px rgba(22,58,90,.12); }

  /* BUTTONS */
  .rspa-btn {
    font-family: var(--rspa-ff-body); font-size: 15px; font-weight: 500;
    padding: 14px 28px; border-radius: 10px; cursor: pointer;
    transition: all .2s; border: none; text-decoration: none; display: inline-flex; align-items: center;
  }
  .rspa-btn-primary { background: linear-gradient(135deg, var(--rspa-sky) 0%, var(--rspa-secondary-deep) 100%); color: #fff; box-shadow: 0 4px 14px rgba(42,182,232,0.35); }
  .rspa-btn-primary:hover { background: linear-gradient(135deg, var(--rspa-sky-hover) 0%, var(--rspa-sky) 100%); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(42,182,232,0.4); }

  /* CTA BAND */
  .rspa-cta-band { background: var(--rspa-bg-dark); padding: 72px 40px; text-align: center; }
  .rspa-cta-band-title { font-family: var(--rspa-ff-head); font-size: 44px; font-weight: 800; color: #fff; margin-bottom: 12px; letter-spacing: -.02em; }
  .rspa-cta-band-sub { font-size: 16px; color: rgba(255,255,255,.45); margin-bottom: 32px; font-weight: 400; }
  .rspa-cta-band-row { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
  .rspa-band-btn { font-family: var(--rspa-ff-body); font-size: 15px; font-weight: 500; padding: 14px 28px; border-radius: 10px; cursor: pointer; transition: all .2s; border: none; text-decoration: none; display: inline-flex; align-items: center; }
  .rspa-band-btn.fill { background: linear-gradient(135deg, var(--rspa-sky) 0%, var(--rspa-secondary-deep) 100%); color: #fff; box-shadow: 0 4px 14px rgba(42,182,232,0.35); }
  .rspa-band-btn.fill:hover { background: linear-gradient(135deg, var(--rspa-sky-hover) 0%, var(--rspa-sky) 100%); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(42,182,232,0.45); }
  .rspa-band-btn.ghost { background: transparent; border: 1.5px solid rgba(255,255,255,.25); color: rgba(255,255,255,.65); }
  .rspa-band-btn.ghost:hover { border-color: #fff; color: #fff; }

  @media (max-width: 1024px) {
    .rspa-h1 { font-size: 48px; }
    .rspa-h2 { font-size: 36px; }
    .rspa-grid { gap: 48px; grid-template-columns: 1fr; }
    .rspa-grid.reverse { grid-template-columns: 1fr; }
  }
  @media (max-width: 600px) {
    .rspa-h1 { font-size: 40px; }
    .rspa-h2 { font-size: 32px; }
    .rspa-grid { gap: 40px; }
    .rspa-grid.reverse { direction: ltr; }
    .rspa-section, .rspa-section-alt { padding: 64px 24px; }
    .rspa-hero { padding: 64px 24px 0px; }
  }
`;

function RefPartners() {
  const { t } = useTranslation('partners');
  const navigate = useNavigate();

  return (
    <div className="rspa-page">
      <style>{PARTNER_STYLES}</style>

      {/* HERO */}
      <div className="rspa-hero">
        <div className="rspa-hero-orb" />
        <div className="rspa-hero-content">
          <h1 className="rspa-h1">
            {t('hero.titlePrefix')}<span className="rspa-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rspa-hero-sub">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      {/* RESELLERS SECTION */}
      <div className="rspa-section">
        <div className="rspa-grid">
          <div className="rspa-text-block">
            <div className="rspa-section-label">{t('resellers.label')}</div>
            <h2 className="rspa-h2">{t('resellers.title')}</h2>
            <p className="rspa-p">
              {t('resellers.description')}
            </p>
            <div style={{ marginTop: "32px" }}>
              <button
                type="button"
                className="rspa-btn rspa-btn-primary"
                onClick={() => navigate("/contact")}
              >
                {t('resellers.learnMore')}
              </button>
            </div>
          </div>
          <div className="rspa-image-wrap">
            <img src={resellers} alt={t('resellers.title')} className="rspa-img" />
          </div>
        </div>
      </div>

      {/* NON-PROFITS SECTION */}
      <div className="rspa-section">
        <div className="rspa-grid">
          <div className="rspa-image-wrap">
            <img src={non_profits} alt={t('affiliates.title')} className="rspa-img" />
          </div>
          <div className="rspa-text-block">
            <div className="rspa-section-label">{t('affiliates.label')}</div>
            <h2 className="rspa-h2">{t('affiliates.title')}</h2>
            <p className="rspa-p">
              {t('affiliates.p1')}
            </p>
            <p className="rspa-p">
              {t('affiliates.p2')}
            </p>
            <div style={{ marginTop: "32px" }}>
              <button
                type="button"
                className="rspa-btn rspa-btn-primary"
                onClick={() => navigate("/contact")}
              >
                {t('affiliates.learnMore')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <RefPricingTeaser />
      <RefSiteFooter />
    </div>
  );
}

export default RefPartners;
